import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJobs, updateJob, deleteJob, fetchDescription } from "../services/api";
import ScoreBadge from "../components/ScoreBadge";

const GERMAN_WORDS = [
  "für", "und", "oder", "schwerpunkt", "ingenieur", "architekt",
  "automatisierung", "containertechnologien", "deutschkenntnisse",
  "stellenangebot", "stellenanzeig",
  "m/w/d", "(m/w/d)", ":innen", "*innen", "/innen", "willkommen",
  "entwickler", "berater", "leiter",
  "absolvent", "absolventin", "einstieg", "berufseinsteiger",
];

const ENGLISH_PHRASES = [
  "working language is english",
  "working language: english",
  "team language is english",
  "english speaking environment",
  "english speaking team",
  "english is our working language",
  "english is the working language",
  "fluent in english",
  "fluent english required",
  "business english",
  "excellent english",
  "strong english",
  "english communication skills",
  "written and spoken english",
  "english is a must",
  "english proficiency",
  "english language required",
  "english as the company language",
];

const GERMAN_REQUIRED_PHRASES = [
  "german required", "german is required", "german a must", "german is a must",
  "german mandatory", "german is mandatory", "german essential", "german is essential",
  "german c1", "german c2", "german b2", "german b1",
  "fluent german", "fluent in german", "german language required",
  "german language skills", "german speaking", "german-speaking",
  "german speaker", "native german", "german mother tongue",
  "german as mother tongue", "german native speaker", "mother tongue german",
  "deutsch erforderlich", "deutschkenntnisse", "sprachkenntnisse deutsch",
  "german proficiency", "must speak german", "must have german",
  "good command of german", "excellent german", "professional german",
  "advanced german", "language: german", "languages: german",
  "required languages: german", "language requirements: german",
  "verhandlungssicheres deutsch", "verhandlungssicher in deutsch",
  "deutsch in wort und schrift", "muttersprache deutsch",
  "deutsch als muttersprache", "deutschkenntnisse erforderlich",
  "gute deutschkenntnisse", "sehr gute deutschkenntnisse",
  "deutsch auf muttersprachlichem niveau", "deutsch zwingend",
];

// Catches "German is not required", "German is a plus", etc — wins over a
// proximity match so we don't filter jobs where German is merely a bonus.
const GERMAN_NEGATION_PHRASES = [
  "german is a plus", "german is an advantage", "german is a bonus",
  "german is beneficial", "german is nice to have", "german is optional",
  "german would be a plus", "german would be an advantage",
  "german not required", "no german required", "german is not required",
  "german is not a must", "german skills are a plus",
  "german language skills are a plus", "knowledge of german is a plus",
  "basic german is a plus", "german desirable but not essential",
  "german nice to have", "willingness to learn german",
  "deutschkenntnisse von vorteil", "deutsch von vorteil",
  "deutschkenntnisse sind ein plus", "deutschkenntnisse wünschenswert",
  "no german needed", "german not needed", "german not necessary",
];

// "German (C1)", "C1 German", "German native speaker", etc.
const GERMAN_LEVEL_REGEX = /\b(german|deutsch)\b[\s:()\-]{0,6}(c1|c2|b1|b2)\b|\b(c1|c2|b1|b2)\b[\s:()\-]{0,6}(german|deutsch)\b|\b(german|deutsch)\b[\s\w]{0,15}(native speaker|mother tongue|muttersprache)/i;

// "German is required", "Required: German", "fluent German required", etc.
const GERMAN_PROXIMITY_REGEX = /\b(german|deutsch)\b[\s\w,]{0,25}\b(required|requirement|requires|fluent|mandatory|essential|must|necessary)\b|\b(required|requirement|requires|fluent|mandatory|essential|must|necessary)\b[\s\w,]{0,25}\b(german|deutsch)\b/i;

// Bare "German" as a standalone word in the title, e.g. "Customer Success Manager - German"
// or "Cloud Support Engineer (German & English speaking)" — \b excludes "Germany" (a location).
const GERMAN_WORD_IN_TITLE_REGEX = /\bgerman\b/i;
const BLOCKED_LANGUAGE_REGEX = /\b(german|deutsch|dutch|nederlands|french|francais|swedish|svenska|danish|dansk|norwegian|norsk|finnish|suomi|spanish|espanol|polish|polski|portuguese|portugues|italian|italiano|czech|romanian|hungarian|magyar|turkish|turkce|arabic|mandarin|cantonese|chinese|japanese|korean|russian)\b/i;
const GERMAN_TITLE_MARKER_REGEX = /(m\/w\/d|w\/m\/d|w\/m\/x|m\/f\/d|f\/m\/d|d\/f\/m|m\/f\/x|m\|w\|d|w\|m\|d|m\|f\|d|w\|m\|x|m\/w\/f|:innen|\*innen|\/innen|praktikant\*in|deutschkennt|deutschsprach|stellenangebot|stellenanzeig|entwickler|berater|ingenieur|werkstudent|berufseinsteiger|pharmaziepraktik|systemadministrator|tage-woche|monetarisierung|industrie|mensch)/i;
const GERMAN_REQUIREMENT_TEXT_REGEX = /deutschkennt|deutschsprach|(?:deutsche|deutschen|deutscher)\s+sprache|(?:kenntnisse|kommunikation)\b.{0,30}\bdeutsch|(?:fließend|fliessend|verhandlungssicher)\b.{0,20}\bdeutsch/i;
const EXCLUDED_SKILL_REQUIREMENT_REGEX = /\bstrong\s+proficien(?:cy|t)\b[\s\S]{0,60}\bprogramming languages?\b|\bstrong\b[\s\S]{0,30}\bprogramming languages?\b[\s\S]{0,30}\bproficien(?:cy|t)\b/i;
const GERMAN_TITLE_WORD_REGEX = /(?:^|\s)(?:für|schwerpunkt|automatisierung|willkommen|absolvent(?:in)?|einsteiger|einstieg|leiter)(?:\s|$)|[äöüß]/i;

function hasBlockedLanguage(job) {
  const title = job.title || "";
  const combined = `${title}\n${job.description || ""}`;
  if (GERMAN_TITLE_MARKER_REGEX.test(title)) return true;
  if (GERMAN_TITLE_WORD_REGEX.test(title)) return true;
  if (GERMAN_REQUIREMENT_TEXT_REGEX.test(combined)) return true;
  if (EXCLUDED_SKILL_REQUIREMENT_REGEX.test(combined)) return true;
  if (BLOCKED_LANGUAGE_REGEX.test(combined)) return true;
  return /[日本語韓国어русскийالعربية]/i.test(combined);
}

const SPECIALIST_STACKS = [
  "angular developer", "angular engineer", "angular frontend",
  "vue developer", "vue.js developer", "vue engineer",
  "php developer", "php backend", "php engineer",
  "wordpress developer", "wordpress engineer",
  "drupal developer", "magento developer",
  "sharepoint developer", "sharepoint engineer",
  "salesforce developer", "salesforce admin", "salesforce engineer",
  "sap developer", "sap consultant", "sap engineer",
  "ios developer", "ios engineer", "ios app developer",
  "android developer", "android engineer",
  "swift developer", ".net developer",
  "ruby developer", "ruby on rails",
  "kotlin developer",
];

// Stacks outside what the user actually works in. Matched on the title with
// word boundaries, so "JavaScript" is untouched by the Java rule -- frontend
// and full-stack roles stay in the feed.
// Ranking tier by location. Berlin is where the user is, so those come first,
// then the rest of Germany, then remote roles reachable from it, then the rest.
// Applied before score, because a higher-scoring job in Warsaw is still not a
// job the user can take.
// German cities other than Berlin. Without these, a Munich listing ranked
// alongside London, because only "berlin" and the literal word "germany"
// were recognised and Arbeitnow rarely spells out the country.
const GERMAN_CITIES = [
  "munich", "münchen", "muenchen", "hamburg", "frankfurt", "cologne", "köln",
  "koeln", "stuttgart", "düsseldorf", "duesseldorf", "dusseldorf", "leipzig",
  "dortmund", "essen", "bremen", "dresden", "hannover", "hanover", "nuremberg",
  "nürnberg", "nuernberg", "karlsruhe", "mannheim", "bonn", "münster",
  "muenster", "augsburg", "wiesbaden", "bielefeld", "potsdam", "heidelberg",
  "freiburg", "darmstadt", "jena", "ulm", "aachen", "kiel", "mainz",
];

// Ranking tier by location. Berlin is where the user is, so those come first,
// then the rest of Germany, then remote roles reachable from it, then the rest.
// Applied before score, because a higher-scoring job in Warsaw is still not a
// job the user can take.
function locationRank(job) {
  const loc = (job.location || "").toLowerCase();
  if (loc.includes("berlin")) return 0;
  if (loc.includes("germany") || loc.includes("deutschland")) return 1;
  if (GERMAN_CITIES.some((c) => loc.includes(c))) return 1;
  // Israel is a target country on the same footing as the rest of Germany.
  if (isIsrael(job)) return 1;
  if (job.isRemoteEU || loc.includes("remote")) return 2;
  return 3;
}

// Berlin including its suburbs, which Adzuna spells out instead of the city
// ("Potsdam", "Teltow") and which are inside the S-Bahn ring for commuting.
const BERLIN_AREA = [
  "berlin", "potsdam", "teltow", "kleinmachnow", "falkensee", "hennigsdorf",
  "oranienburg", "bernau", "schonefeld", "schönefeld", "koenigs wusterhausen",
  "königs wusterhausen", "strausberg", "eichwalde", "zeuthen",
];
function isBerlin(job) {
  const loc = (job.location || "").toLowerCase();
  return BERLIN_AREA.some((c) => loc.includes(c));
}

// The scraper flags Israeli rows (Drushim, Israeli company boards, LinkedIn's
// Israel searches); the location test covers rows scraped before the flag.
function isIsrael(job) {
  return job.companyCountry === "🇮🇱" || (job.location || "").toLowerCase().includes("israel");
}

// Whether the German filter actually got to read this posting.
//
// Adzuna's API returns a 500-character snippet, and a German requirement is
// almost always stated at the end: of the long postings that mention German at
// all, 108 of 139 first mention it past the halfway point. So a snippet row
// carries no information either way -- the filter passed it because it saw
// nothing, not because it saw nothing wrong.
//
// The API sets descriptionTruncated when the stored text ran past its 900-char
// list budget, which only happens on a real posting. Anything at or under the
// Adzuna cap and not truncated is a snippet.
const ADZUNA_SNIPPET_CAP = 520;
function langChecked(job) {
  if (job.descriptionTruncated) return true;
  return (job.description || "").length > ADZUNA_SNIPPET_CAP;
}

// Staffing agencies and aggregators that repost the same roles under their own
// name. They were the worst offenders in the duplicate count.
const BLOCKED_COMPANIES = [
  "instaffo", "mindrift", "epam", "experis", "hays", "michael page",
  "robert half", "randstad", "adecco", "manpower", "gulp", "brunel",
  "darwin recruitment", "austin fraser", "nicoll curtin", "empresaria",
];

function isBlockedCompany(job) {
  const c = (job.company || "").toLowerCase();
  return BLOCKED_COMPANIES.some((b) => c.includes(b));
}

// "English" is only set when the posting says so outright; a listing that
// names no language at all is treated as English here, since every other
// language is already a rejection by the time a row reaches the feed.
function looksEnglish(job) {
  if (job.postingLanguage === "de") return false;
  return !job.langWarning;
}

// Non-technical roles that share a job title with technical ones. Adzuna's
// "Junior Developer" search returns "Junior Recipe Developer" (a cooking job)
// and French "Business Developer" sales roles; the backend sales list had
// "business development" but not "business developer".
//
// A bare "chef" is deliberately absent: "chef de section" is French for head
// of department, and one such row is a genuine senior cloud engineer role.
const NON_TECH_ROLE_RE =
  /\brecipe\b|\bculinary\b|\bpastry\b|\bbarista\b|\bsous chef\b|\bhead chef\b|\bchef de cuisine\b|\bkitchen\b|\bbusiness develop|\bsales\b|\bvertrieb\b|d[ée]veloppement commercial|ing[ée]nieur commercial|\brecruit|\btalent acquisition\b|\blearning (?:and|&) development\b|\bmarketing\b|\bmarcom\b|נציג|שירות לקוחות|מכירות|כלכלן/i;

const EXCLUDED_ROLE_RE =
  /\bjava\b|\bj2ee\b|\bjakarta ee\b|\bspring boot\b|\bgame\b|\bgames\b|\bgaming\b|\bgameplay\b|\bunity\b|\bunreal\b|\bgamedev\b|\bgodot\b|\bback[- ]?end\b/i;

const JUNIOR_WORDS = [
  "junior", "entry level", "entry-level", "graduate", "trainee",
  "associate", "no experience", "little experience", "0-1 year", "0-2 year",
  "berufseinsteiger", "werkstudent", "absolvent", "absolventin",
  "einsteiger", "einstieg", "praktikant", "praktikantin",
  "azubi", "auszubildende", "nachwuchs", "quereinsteiger",
  "keine erfahrung", "wenig erfahrung", "hochschulabsolvent",
];

const SENIOR_WORDS = [
  "senior", " sr.", " sr ", "lead ", "tech lead", "principal",
  "staff engineer", "head of", "engineering manager", "director",
  "vp of", "vice president", "distinguished",
  "(senior)", "leitender", "teamleiter", "abteilungsleiter",
  "führungskraft", "experte", "erfahrener", "erfahrene",
  "architect", "experienced", " expert", "consultant",
  "5+ years", "6+ years", "7+ years", "8+ years", "10+ years",
];

function detectLangNote(job) {
  if (job.langNote) return job.langNote;
  const t = (job.title || "").toLowerCase();
  const d = (job.description || "").toLowerCase();
  const combined = `${t}\n${d}`;

  if (GERMAN_REQUIRED_PHRASES.some((p) => combined.includes(p))) return "German required";
  if (GERMAN_WORD_IN_TITLE_REGEX.test(t)) return "German required";
  if (GERMAN_LEVEL_REGEX.test(combined)) return "German required";
  if (
    GERMAN_PROXIMITY_REGEX.test(combined) &&
    !GERMAN_NEGATION_PHRASES.some((p) => combined.includes(p))
  ) {
    return "German required";
  }

  const hasGerman = GERMAN_WORDS.some((w) => t.includes(w)) || /[äöüß]/.test(t);
  if (hasGerman) return "German likely";
  return "";
}

function isEnglishFriendly(job) {
  if (["English", "Hebrew"].includes(job.langNote || "")) return true;
  if (/hebrew|עברית/i.test(`${job.title || ""}\n${job.description || ""}`)) return true;
  const t = (job.title || "").toLowerCase();
  const d = (job.description || "").toLowerCase().slice(0, 500);
  return ENGLISH_PHRASES.some((p) => t.includes(p) || d.includes(p));
}

function isJuniorRole(job) {
  if (job.isJunior !== undefined) return job.isJunior;
  const t = (job.title || "").toLowerCase();
  return JUNIOR_WORDS.some((w) => t.includes(w));
}

function isSeniorRole(job) {
  const t = (job.title || "").toLowerCase();
  if (SENIOR_WORDS.some((w) => t.includes(w))) return true;
  return job.isSenior === true;
}

function isNoise(job) {
  const t = (job.title || "").toLowerCase();
  if (hasBlockedLanguage(job)) return true;
  const researchWords = ["phd", "ph.d", "doctorate", "postdoc", "research scientist", "research engineer", "doktorand", "data scientist"];
  const salesWords = ["sales engineer", "pre-sale", "presale", "solutions engineer", "solution engineer", "account executive", "account manager"];
  const supportWords = ["helpdesk", "help desk", "1st line", "it support", "desktop support", "service desk"];
  const cloudWords = ["aws", "azure", "gcp", "cloud", "devops", "kubernetes", "docker", "terraform"];
  if (researchWords.some((w) => t.includes(w))) return true;
  if (salesWords.some((w) => t.includes(w))) return true;
  if (supportWords.some((w) => t.includes(w)) && !cloudWords.some((w) => t.includes(w))) return true;
  if (SPECIALIST_STACKS.some((w) => t.includes(w))) return true;
  if (EXCLUDED_ROLE_RE.test(t)) return true;
  if (NON_TECH_ROLE_RE.test(t)) return true;
  if (isBlockedCompany(job)) return true;
  if (isSeniorRole(job)) return true;
  if (job.langWarning) return true;
  const lang = detectLangNote(job);
  if (lang === "German required" || lang === "German likely") return true;
  return false;
}

// One switch that keeps only what the CV can actually land, read at its real
// level rather than its headline: about two years of employed frontend work
// (2020-22) with QA in both jobs, freelance serverless and AI-pipeline builds
// on AWS since, a Cloud Practitioner cert, a QA-automation course, and early
// support / fraud-ops work. So frontend, full-stack JS, software QA and
// technical support are in reach up to three years asked; cloud, DevOps, AI
// and generic engineering titles only at entry level; everything else is out.
// It runs on top of the normal noise filter and changes nothing else.

// Role families the CV supports, checked in this order.
const CV_FAMILIES = [
  ["qa", /\bqa\b|\bquality assurance\b|\btest(?:ing)? (?:automation|engineer|analyst)\b|\bautomation tester\b|\btester\b|\bsdet\b/i],
  ["support", /\bapplication support\b|\btechnical (?:customer )?support\b|\bsupport (?:engineer|specialist)\b|\bproduct support\b|\bcloud support\b|\btechnical operations\b|\b(?:deployment|implementation) engineer\b/i],
  ["frontend", /\bfront[- ]?end\b|\breact\b|\bnext\.?js\b|\bweb (?:developer|engineer)\b|\bjavascript\b|\btypescript\b|\bui (?:developer|engineer)\b/i],
  ["fullstack", /\bfull[- ]?stack\b|\bnode(?:\.?js)?\b/i],
  ["ai", /\bprompt engineer\b|\bai (?:engineer|developer|automation)\b|\bagentic\b|\bgenai\b|\bllm\b/i],
  ["cloud", /\bcloud\b|\baws\b|\bdevops\b|\bserverless\b|\binfrastructure (?:engineer|support)\b|\bplatform engineer\b|\bsre\b|\bsite reliability\b/i],
  ["generic", /\bsoftware (?:engineer|developer)\b|\bdeveloper\b|\bprogrammer\b|\bentwickler\b|מפתח/i],
];
// Families the CV only reaches at entry level.
const CV_ENTRY_ONLY = new Set(["ai", "cloud", "generic"]);

// Never in reach, whatever the family: other disciplines, research, and
// "quality" in the factory or lab sense rather than software testing.
const CV_OFF_RE = /\bdata (?:engineer|scien|analy|architect|platform)|\banalytics engineer\b|\bmachine learning\b|\bml\b|\bmlops\b|\bai (?:trainer|tutor|data)\b|\bresearch|\brobotic|\bembedded\b|\bfirmware\b|\bhardware\b|\bfpga\b|\bplc\b|\bmechanical\b|\belectrical\b|\bquant(?:itative)?\b|\bdesigner\b|\bproduct (?:manager|owner)\b|\bproject manager\b|\bscrum master\b|\bconsultant\b|\barchitect\b|\bmember of technical staff\b|\bpenetration\b|\bpentest|\bpharma|\bmanufactur|\bnutrition\b|\bfood\b|\bmedical\b|\bclinical\b|\bgmp\b|\bregulatory\b|\bsupplier\b|\blaborator|\bchemical\b|\bsap\b|\bfinops\b|\bplanner\b|\badvocate\b/i;
// Stacks the CV does not have, for the families where the stack is the job.
// QA and support roles are left alone: testing a mobile app is still QA.
const CV_STACK_OFF_RE = /\bc\+\+|\bc#|\.net\b|\bnet\b|\bgolang\b|\bgo developer\b|&\s*go\b|\bgo\b(?= \(|\s*$)|\brust\b|\bscala\b|\belixir\b|\bangular\b|\bvue\b|\bkotlin\b|\bmobile\b|\bios\b|\bandroid\b|\bflutter\b|\bsecurity\b|\bnetwork engineer\b/i;
// Open only to students or new graduates, written for a Romance-language
// market the CV has no language for, or asking for an expert ("מנוסה",
// "מומחה", tier 3). Hebrew is matched bare: \b never fires next to it in JS.
const CV_INELIGIBLE_RE = /\bintern(?:ship)?\b|\bworking student\b|\bwerkstudent|\bstudent\b|\bthesis\b|\bgraduate\b|\bstage\b|\balternance\b|\bapprentice|\bausbildung\b|\bh\/f\b|\bf\/h\b|\bcdi\b|\bdesarrollador\b|\bprogramador\b|\bsviluppatore\b|\bd[ée]veloppeur|\bing[ée]nieur|\bingeniero\b|\bingegnere\b|מנוסה|מומחה|\btier 3\b|\bl3\b/i;
const CV_ENTRY_RE = /\bjunior\b|\bjr\.?(?=\s|$)|\bentry[- ]level\b|\bassociate\b|\btrainee\b|\bberufseinsteiger|\bnachwuchs|\bquereinsteiger|ג['׳]וניור|ללא ניסיון|בוגר/i;
// Years asked: three is the stretch for the roles the CV has done, two for
// the ones it has only built on its own.
const CV_MAX_YEARS = 3;
const CV_ENTRY_YEARS = 2;
// "Quality assurance" alone is as likely a factory as a codebase, so it needs
// a software word somewhere in the title or the preview.
const CV_SOFTWARE_RE = /software|automation|selenium|cypress|playwright|\bapi\b|\bweb\b|\bapp\b|jira|\bbug|test case|\bsaas\b|\bsql\b|javascript|python/i;

function cvRoleFamily(title) {
  const hit = CV_FAMILIES.find(([, re]) => re.test(title));
  return hit ? hit[0] : null;
}

function isEntryLevel(job) {
  if (CV_ENTRY_RE.test(job.title || "")) return true;
  return job.yearsRequired != null && job.yearsRequired <= CV_ENTRY_YEARS;
}

function isCvMatch(job) {
  const t = job.title || "";
  if (CV_OFF_RE.test(t) || CV_INELIGIBLE_RE.test(t)) return false;
  const family = cvRoleFamily(t);
  if (!family) return false;
  if (family !== "qa" && family !== "support" && CV_STACK_OFF_RE.test(t)) return false;
  if (family === "qa" && !/engineer|automation|software|test|sdet|\bqa\b/i.test(t)
      && !CV_SOFTWARE_RE.test(job.description || "")) return false;
  if (CV_ENTRY_ONLY.has(family) && !isEntryLevel(job)) return false;
  if (job.yearsRequired != null && job.yearsRequired > CV_MAX_YEARS) return false;
  // Reachable from Berlin without moving abroad: Berlin, the rest of Germany,
  // Israel, or remote.
  return locationRank(job) <= 2;
}

function useSeenJobs() {
  const [seenIds, setSeenIds] = useState(() => new Set());

  const markSeen = useCallback((jobId) => {
    setSeenIds((prev) => {
      if (prev.has(jobId)) return prev;
      const next = new Set(prev);
      next.add(jobId);
      localStorage.setItem("seenJobs", JSON.stringify([...next]));
      return next;
    });
  }, []);

  return { seenIds, markSeen };
}

function RelativeDate({ dateStr }) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return <span>Today</span>;
  if (diffDays === 1) return <span>Yesterday</span>;
  if (diffDays < 7) return <span>{diffDays}d ago</span>;
  return <span>{date.toLocaleDateString()}</span>;
}

function JobCard({ job, onDelete, onMove, isDeleting, isMoving, seenIds, markSeen }) {
  // The list response carries a capped preview and no requirements at all, so
  // that 2900 rows stay under Lambda's 6 MB response limit. The posting and
  // the requirements pulled out of it come from one request per card, made
  // when the card is opened.
  const [fullText, setFullText] = useState("");
  const [requirements, setRequirements] = useState("");
  const [loadingFull, setLoadingFull] = useState(false);
  const loadFull = async () => {
    setLoadingFull(true);
    try {
      const r = await fetchDescription(job.jobId);
      setFullText(r.description || "");
      setRequirements(r.requirements || "");
    } catch {
      setFullText("Could not load the full posting.");
    } finally {
      setLoadingFull(false);
    }
  };
  const [expanded, setExpanded] = useState(false);
  const lang = detectLangNote(job);
  const junior = isJuniorRole(job);
  const englishOk = isEnglishFriendly(job);
  const seen = seenIds.has(job.jobId);
  const isRemote = job.isRemoteEU || (job.location || "").toLowerCase().includes("remote");
  const isNew = job.dateScraped && (Date.now() - new Date(job.dateScraped).getTime()) < 24 * 60 * 60 * 1000;

  const handleExpand = () => {
    const opening = !expanded;
    setExpanded((v) => !v);
    if (opening && !fullText && !loadingFull) loadFull();
    if (!opening) markSeen(job.jobId);
  };

  return (
    <div className={`rounded-xl border transition-shadow ${seen ? "opacity-60" : ""} ${
      isRemote
        ? "bg-indigo-50/70 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800/60"
        : junior
        ? "bg-blue-50/70 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800/60"
        : "bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800"
    }`}>

      {/* Tap/click header */}
      <button className="w-full text-left px-4 pt-4 pb-3 focus:outline-none touch-manipulation" onClick={handleExpand}>
        {/* Badges row */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          {isNew && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">New</span>
          )}
          {junior && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800">Junior</span>
          )}
          {isRemote && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800">
              {job.isRemoteEU ? "Remote EU" : "Remote"}
            </span>
          )}
          {job.source && (
            <span
              title={`Found via ${job.source}`}
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
            >
              {job.source}
            </span>
          )}
          {job.postingLanguage === "de" && (
            <span
              title="This ad is written in German, or carries a German-market marker. That does not necessarily mean the job requires German — most of these listings are truncated before the requirements section."
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800"
            >
              DE ad
            </span>
          )}
          {!langChecked(job) && (
            <span
              title="Only a 500-character preview of this ad exists, and a German requirement is usually stated at the end. Nobody has checked this one -- the filter saw nothing, which is not the same as finding nothing."
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800"
            >
              German unchecked
            </span>
          )}
          {(job.isRemoteEU || isIsrael(job)) && job.companyCountry && <span className="text-base">{job.companyCountry}</span>}
          {seen && <span className="text-xs font-medium text-gray-300 dark:text-gray-600">Seen</span>}
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            <ScoreBadge score={job.score} />
            {job.hasSalary && <span className="text-sm">💰</span>}
            {englishOk && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">EN</span>
            )}
            <svg className={`w-4 h-4 text-gray-300 dark:text-gray-600 transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h3 dir="auto" className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug mb-1">
          {job.title}
        </h3>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
          <span className="font-medium text-gray-600 dark:text-gray-300">{job.company}</span>
          <span>·</span>
          <span className="truncate max-w-[160px] sm:max-w-none">{job.location}</span>
          <span>·</span>
          <RelativeDate dateStr={job.dateScraped} />
        </div>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="px-4 pb-4 pt-3 space-y-3 border-t border-gray-100 dark:border-gray-800">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
              <p className="text-gray-400 dark:text-gray-500 mb-0.5">Source</p>
              <p className="font-medium text-gray-700 dark:text-gray-300">{job.source || "unknown"}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
              <p className="text-gray-400 dark:text-gray-500 mb-0.5">Language</p>
              <p className="font-medium text-gray-700 dark:text-gray-300">{lang || "Not specified"}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
              <p className="text-gray-400 dark:text-gray-500 mb-0.5">Level</p>
              <p className="font-medium text-gray-700 dark:text-gray-300">
                {junior ? "Junior" : isSeniorRole(job) ? "Senior" : "Mid-level"}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
              <p className="text-gray-400 dark:text-gray-500 mb-0.5">Remote</p>
              <p className="font-medium text-gray-700 dark:text-gray-300">
                {job.isRemoteEU ? "Remote EU" : job.location?.includes("Remote") ? "Yes" : "On-site"}
              </p>
            </div>
          </div>

          {requirements && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2">
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">What they look for</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {requirements}
              </p>
            </div>
          )}

          {job.descriptionTruncated && !fullText && (
            <button
              onClick={loadFull}
              disabled={loadingFull}
              className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
            >
              {loadingFull ? "Loading…" : "Show full posting"}
            </button>
          )}
          {fullText && (
            <p dir="auto" className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {fullText}
            </p>
          )}
          {!fullText && job.description && (
            <div dir="auto" className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 leading-relaxed line-clamp-6">
              {job.description}
            </div>
          )}

          {/* Actions — full width on mobile */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
            {job.url && (
              <a
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center py-2.5 sm:py-1.5 px-3 text-sm sm:text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors touch-manipulation"
              >
                Apply
              </a>
            )}
            <button
              onClick={() => onMove(job.jobId)}
              disabled={isMoving}
              className="flex items-center justify-center py-2.5 sm:py-1.5 px-3 text-sm sm:text-xs font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors touch-manipulation"
            >
              Mark Applied
            </button>
            <button
              onClick={() => onDelete(job.jobId)}
              disabled={isDeleting}
              className="col-span-2 sm:col-span-1 sm:ml-auto flex items-center justify-center py-2.5 sm:py-1.5 px-3 text-sm sm:text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:border-red-800 disabled:opacity-50 transition-colors touch-manipulation"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobFeed() {
  const queryClient = useQueryClient();
  const { seenIds, markSeen } = useSeenJobs();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: jobs = [], isLoading, error } = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ jobId }) => deleteJob(jobId),
    onSuccess: (_data, { jobId }) => {
      // Drop the row from the cache rather than refetching /jobs: that call
      // returns several MB and takes seconds, during which the card stayed on
      // screen and the delete looked like it had done nothing.
      queryClient.setQueryData(["jobs"], (old) =>
        (old || []).filter((j) => j.jobId !== jobId)
      );
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const moveMutation = useMutation({
    mutationFn: ({ jobId }) =>
      updateJob(jobId, { status: "applied", dateApplied: new Date().toISOString() }),
    onSuccess: (_data, { jobId }) => {
      queryClient.setQueryData(["jobs"], (old) =>
        (old || []).map((j) =>
          j.jobId === jobId ? { ...j, status: "applied" } : j
        )
      );
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const [keyword, setKeyword] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [sortBy, setSortBy] = useState("smart");
  const [hideEU, setHideEU] = useState(false);
  const [hideSeen, setHideSeen] = useState(false);
  const [hideGerman, setHideGerman] = useState(false);
  const [englishOnly, setEnglishOnly] = useState(false);
  const [onlyNew, setOnlyNew] = useState(false);
  const [berlinOnly, setBerlinOnly] = useState(false);
  const [israelOnly, setIsraelOnly] = useState(false);
  const [checkedOnly, setCheckedOnly] = useState(false);
  const [cutThroat, setCutThroat] = useState(false);
  const [activeCategories, setActiveCategories] = useState(new Set());

  const CATEGORIES = [
    { label: "Cloud/DevOps", words: ["cloud", "devops", "aws", "sre", "platform", "infrastructure", "site reliability"] },
    { label: "Azure", words: ["azure"] },
    { label: "AI/ML", words: ["ai ", " ai", "machine learning", "generative", "prompt", "mlops", "full stack ai", "ai engineer", "ai frontend", "ai trainer"] },
    { label: "Junior Dev", words: ["junior", "associate", "graduate", "entry level", "entry-level"] },
    { label: "Full Stack", words: ["full stack", "fullstack"] },
    { label: "Frontend", words: ["frontend", "front-end", "front end"] },
    { label: "QA/Support", words: ["qa ", "quality assurance", "automation engineer", "support engineer", "application support"] },
    { label: "Data", words: ["data analyst", "data engineer"] },
    { label: "Pharma", words: ["medical device", "qa pharmaceutical", "regulatory affairs", "regulatory", "pharma", "medtech", "life science", "life sciences", "clinical"] },
  ];

  const toggleCategory = (label) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let list = jobs.filter((j) => j.status === "new");
    if (keyword) {
      const kw = keyword.toLowerCase();
      list = list.filter(
        (j) =>
          j.title.toLowerCase().includes(kw) ||
          j.company.toLowerCase().includes(kw) ||
          j.location.toLowerCase().includes(kw)
      );
    }
    if (activeCategories.size > 0) {
      const matchWords = CATEGORIES
        .filter((c) => activeCategories.has(c.label))
        .flatMap((c) => c.words);
      list = list.filter((j) => {
        const t = j.title.toLowerCase();
        return matchWords.some((w) => t.includes(w));
      });
    }
    list = list.filter((j) => j.score >= minScore && j.score <= maxScore);
    list = list.filter((j) => !isNoise(j));
    if (hideEU) list = list.filter((j) => !j.isRemoteEU);
    if (hideSeen) list = list.filter((j) => !seenIds.has(j.jobId));
    if (hideGerman) list = list.filter((j) => j.postingLanguage !== "de");
    if (englishOnly) list = list.filter(looksEnglish);
    if (berlinOnly) list = list.filter(isBerlin);
    if (israelOnly) list = list.filter(isIsrael);
    if (checkedOnly) list = list.filter(langChecked);
    if (cutThroat) list = list.filter(isCvMatch);
    if (onlyNew) {
      const cutoff = Date.now() - 86400000;
      list = list.filter((j) => new Date(j.dateScraped).getTime() >= cutoff);
    }
    list.sort((a, b) => {
      if (sortBy === "date") return new Date(b.dateScraped) - new Date(a.dateScraped);
      // Smart: reachable location first, then the extracted score, then recency.
      const aLoc = locationRank(a);
      const bLoc = locationRank(b);
      if (aLoc !== bLoc) return aLoc - bLoc;
      // Then postings the German filter could actually read. An unchecked row
      // may still be fine, but it is the one that wastes an afternoon.
      const aChk = langChecked(a) ? 0 : 1;
      const bChk = langChecked(b) ? 0 : 1;
      if (aChk !== bChk) return aChk - bChk;
      const aScore = Number(a.score) || 0;
      const bScore = Number(b.score) || 0;
      if (bScore !== aScore) return bScore - aScore;
      const aNew = Date.now() - new Date(a.dateScraped).getTime() < 86400000 ? 1 : 0;
      const bNew = Date.now() - new Date(b.dateScraped).getTime() < 86400000 ? 1 : 0;
      if (bNew !== aNew) return bNew - aNew;
      return new Date(b.dateScraped) - new Date(a.dateScraped);
    });
    return list;
  }, [jobs, keyword, minScore, maxScore, sortBy, hideEU, hideSeen, hideGerman, englishOnly, onlyNew, berlinOnly, israelOnly, checkedOnly, cutThroat, seenIds, activeCategories]);

  // What the cut-throat switch would leave of the current view, shown on it.
  const cvMatchCount = useMemo(() => filtered.filter(isCvMatch).length, [filtered]);

  const activeFilterCount = [
    keyword, hideEU, hideSeen,
    minScore > 0, maxScore < 100,
    hideGerman,
    englishOnly,
    onlyNew,
    berlinOnly,
    israelOnly,
    checkedOnly,
    cutThroat,
    activeCategories.size > 0,
  ].filter(Boolean).length;

  if (isLoading) {
    return (
      <div className="space-y-3 py-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 animate-pulse">
            <div className="h-3.5 bg-gray-100 dark:bg-gray-800 rounded w-2/3 mb-3" />
            <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-600 dark:text-red-400">
        Failed to load jobs: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Category chips */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => {
          const active = activeCategories.has(cat.label);
          return (
            <button
              key={cat.label}
              onClick={() => toggleCategory(cat.label)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors touch-manipulation ${
                active
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400"
              }`}
            >
              {cat.label}
            </button>
          );
        })}
        {activeCategories.size > 0 && (
          <button
            onClick={() => setActiveCategories(new Set())}
            className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors touch-manipulation"
          >
            Clear
          </button>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
        {/* Search row — always visible */}
        <div className="p-3 flex gap-2">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 dark:text-gray-600 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search title, company, location…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 sm:py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
            />
          </div>
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={`relative shrink-0 px-3 py-2.5 sm:py-2 rounded-lg border text-sm font-medium transition-colors touch-manipulation ${
              filtersOpen || activeFilterCount > 1
                ? "border-brand-500 text-brand-600 bg-brand-50 dark:bg-brand-900/20 dark:text-brand-400"
                : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            {activeFilterCount > 1 && (
              <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-brand-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Expandable filters */}
        {filtersOpen && (
          <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-800 pt-3 space-y-3">
            {/* Score + Sort row */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">Min Score</label>
                <input
                  type="number" min={0} max={100} value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">Max Score</label>
                <input
                  type="number" min={0} max={100} value={maxScore}
                  onChange={(e) => setMaxScore(Number(e.target.value))}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">Sort</label>
                <select
                  value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-2 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                >
                  <option value="smart">Smart</option>
                  <option value="date">Newest</option>
                </select>
              </div>
            </div>

            {/* Toggle row */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: "berlinOnly", label: "Berlin only", value: berlinOnly, set: setBerlinOnly },
                { key: "israelOnly", label: "Israel only", value: israelOnly, set: setIsraelOnly },
                { key: "hideEU", label: "Hide EU remote", value: hideEU, set: setHideEU },
                { key: "hideSeen", label: "Hide seen", value: hideSeen, set: setHideSeen },
                { key: "hideGerman", label: "Hide German-language ads", value: hideGerman, set: setHideGerman },
                { key: "englishOnly", label: "English only", value: englishOnly, set: setEnglishOnly },
                { key: "onlyNew", label: "New since yesterday", value: onlyNew, set: setOnlyNew },
              ].map(({ key, label, value, set }) => (
                <button
                  key={key}
                  onClick={() => set((v) => !v)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors touch-manipulation ${
                    value
                      ? "bg-brand-600 text-white border-brand-600"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results summary */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{filtered.length} job{filtered.length !== 1 ? "s" : ""}</span>
        <button
          onClick={() => setCutThroat((v) => !v)}
          title="Only the roles the CV can realistically land: frontend, full-stack JS, software QA and technical support up to 3 years asked; cloud, DevOps, AI and generic engineering at entry level only; reachable from Berlin."
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors touch-manipulation ${
            cutThroat
              ? "bg-rose-600 text-white border-rose-600"
              : "bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-400 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800"
          }`}
        >
          {cutThroat ? "Cut-throat on · show all" : `Cut-throat (${cvMatchCount})`}
        </button>
        {seenIds.size > 0 && (
          <>
            <span className="text-gray-300 dark:text-gray-700">·</span>
            <span className="text-sm text-gray-400 dark:text-gray-500">{seenIds.size} seen</span>
          </>
        )}
        {/* Adzuna only supplies a 500-char snippet, so its rows can never be
            German-checked; this hides them in one tap without dropping them. */}
        {(checkedOnly || filtered.some((j) => !langChecked(j))) && (
          <button
            onClick={() => setCheckedOnly((v) => !v)}
            title="Adzuna only provides the first 500 characters of each ad, so a German requirement at the end can't be checked."
            className={`ml-auto px-3 py-1.5 rounded-full text-xs font-medium border transition-colors touch-manipulation ${
              checkedOnly
                ? "bg-orange-600 text-white border-orange-600"
                : "bg-orange-50 text-orange-700 border-orange-200 hover:border-orange-400 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800"
            }`}
          >
            {checkedOnly
              ? "German unchecked hidden · show"
              : `Hide German unchecked (${filtered.filter((j) => !langChecked(j)).length})`}
          </button>
        )}
      </div>

      {/* Job list */}
      <div className="space-y-2">
        {filtered.map((job) => (
          <JobCard
            key={job.jobId}
            job={job}
            seenIds={seenIds}
            markSeen={markSeen}
            onDelete={(jobId) => deleteMutation.mutate({ jobId })}
            onMove={(jobId) => moveMutation.mutate({ jobId })}
            isDeleting={
              deleteMutation.isPending &&
              deleteMutation.variables?.jobId === job.jobId
            }
            isMoving={
              moveMutation.isPending &&
              moveMutation.variables?.jobId === job.jobId
            }
          />
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-sm text-gray-400 dark:text-gray-500">
            No jobs match your filters.
          </div>
        )}
      </div>
    </div>
  );
}
